import { useEffect, useState } from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import './Screen.css';

// Helper to get sync status (works in both modes)
const getSyncStatus = async () => {
  try {
    const { AmplifyDataService } = await import('../services/amplifyDataService');
    return await AmplifyDataService.getSyncStatus();
  } catch {
    return { isOnline: navigator.onLine, syncStatus: 'Local mode - no cloud sync' };
  }
};

interface SyncStats {
  isOnline: boolean;
  syncStatus: string;
}

export default function SyncStatus() {
  const [stats, setStats] = useState<SyncStats>({ isOnline: false, syncStatus: 'Unknown' });
  const [loading, setLoading] = useState(true);
  const isOnline = useNetworkStatus();

  const loadData = async () => {
    setLoading(true);
    try {
      const syncStatus = await getSyncStatus();
      const resolvedSyncStatus = 'syncStatus' in syncStatus && (syncStatus as any).syncStatus
        ? (syncStatus as any).syncStatus
        : (syncStatus.isOnline ? 'Online - Auto-syncing' : 'Offline - Will sync when online');
      setStats({
        isOnline: syncStatus.isOnline,
        syncStatus: resolvedSyncStatus,
      });
    } catch (error) {
      console.error('Failed to load sync data:', error);
      setStats({
        isOnline: navigator.onLine,
        syncStatus: 'Local mode - no cloud sync',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [isOnline]);

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Sync Status</h1>
        <p className="screen-subtitle">Monitor and manage data synchronization</p>
      </div>

      <div className="sync-status-overview">
        <div className="status-card">
          <div className="status-icon">{isOnline ? '🟢' : '🔴'}</div>
          <div className="status-content">
            <h3>Connection Status</h3>
            <p className="status-value">{isOnline ? 'Online' : 'Offline'}</p>
          </div>
        </div>
        <div className="status-card">
          <div className="status-icon">🔄</div>
          <div className="status-content">
            <h3>Sync Status</h3>
            <p className="status-value">{stats.syncStatus}</p>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <button
          className="btn btn-secondary"
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading sync status...</div>
      ) : (
        <div className="sync-info">
          <h2>Amplify DataStore Sync Information</h2>
          <div className="info-box">
            <p><strong>How it works:</strong></p>
            <ul>
              <li>DataStore automatically syncs data when online</li>
              <li>All changes are queued locally when offline</li>
              <li>Sync happens automatically when connection is restored</li>
              <li>Conflict resolution is handled automatically</li>
            </ul>
            <p><strong>Current Status:</strong> {stats.syncStatus}</p>
            <p><strong>Network:</strong> {isOnline ? 'Connected' : 'Disconnected'}</p>
          </div>
        </div>
      )}
    </div>
  );
}
